<template>
  <b-card class="shadow-sm">
    <b-card-title
      class="mb-4 pt-6 px-6 d-flex justify-content-between align-items-start"
    >
      <div>
        <b-icon icon="trophy-fill" variant="primary" class="mr-2" />
        <span>Produtos Mais Vendidos</span>
      </div>

      <div class="d-flex mt-1">
        <!-- Filtro de tipo de ranking -->
        <b-dropdown
          :text="parametro.label"
          size="sm"
          variant="light"
          class="filtro-dropdown"
        >
          <b-dropdown-item
            v-for="param in parametros"
            :key="param.id"
            @click="updateParameter(param)"
          >
            {{ param.label }}
          </b-dropdown-item>
        </b-dropdown>

        <!-- Filtro de período -->
        <FiltroPeriodo
          class="ml-3"
          :value="periodo"
          default-label="Hoje"
          :auto-apply-on-mount="!ignoreInitialApply"
          @apply="onApplyPeriodo"
        />
      </div>
    </b-card-title>

    <b-card-body class="pt-2 pb-2 px-6">
      <!-- LOADING (skeletons como os outros cards) -->
      <template v-if="loading">
        <ul class="list-unstyled m-0 p-0">
          <li v-for="i in 5" :key="i" class="d-flex align-items-center mb-6">
            <div
              class="d-flex justify-content-center align-items-center rounded-circle mr-4"
              style="width: 36px; height: 36px; background-color: #f3f6f9"
            >
              <b-skeleton width="18px" height="18px" />
            </div>
            <div class="flex-grow-1" style="width: 100%">
              <div class="d-flex justify-content-between">
                <b-skeleton width="40%" height="16px" />
                <b-skeleton width="20%" height="14px" />
              </div>
              <b-skeleton height="8px" class="mt-2" />
            </div>
          </li>
        </ul>
      </template>

      <!-- LISTA -->
      <template v-else>
        <ul class="list-unstyled m-0 p-0" v-if="sortedProducts.length">
          <li
            v-for="(item, index) in sortedProducts"
            :key="index"
            class="d-flex align-items-center mb-6"
          >
            <div
              class="d-flex justify-content-center align-items-center rounded-circle mr-4"
              :style="{ 'background-color': medalHexColor(index) }"
              style="width: 36px; height: 36px; font-size: 1.5rem"
            >
              <span v-if="index === 0">🥇</span>
              <span v-else-if="index === 1">🥈</span>
              <span v-else-if="index === 2">🥉</span>
              <span v-else class="font-weight-bold" style="font-size: 1.1rem">
                {{ index + 1 }}
              </span>
            </div>

            <div class="flex-grow-1">
              <div class="d-flex justify-content-between">
                <p class="mb-1">{{ item.produto }}</p>
                <small class="text-muted">
                  <span v-if="parametro.id === 'qtd-vendas'">
                    {{ item.qtd }} unidades
                  </span>
                  <span v-else>
                    {{ formatCurrency(item.valor) }}
                  </span>
                </small>
              </div>

              <b-progress
                :value="barValue(item)"
                :max="barMax"
                height="8px"
                :class="{
                  'top1-progress-bar': index === 0,
                  'top2-progress-bar': index === 1,
                  'top3-progress-bar': index === 2,
                  'neutral-progress-bar': index > 2,
                }"
                class="rounded"
              />
            </div>
          </li>
        </ul>

        <div v-else class="text-center py-4">
          <i class="far fa-box-open fa-2x text-muted mb-2"></i>
          <div class="text-muted">Nenhum produto vendido no período</div>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import FiltroPeriodo from "./FiltroPeriodo.vue";

export default {
  name: "TopProdutosVendidos",
  components: { FiltroPeriodo },

  props: {
    /**
     * Aceita:
     * - { data: [{ produto, qtd, valor }] }
     * - ou diretamente um array [{...}]
     */
    dados: {
      type: [Object, Array],
      default: () => ({ data: [] }),
    },
    periodo: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    ignoreInitialApply: { type: Boolean, default: true },
  },

  data() {
    return {
      parametros: [
        { id: "qtd-vendas", label: "Qtd. vendas" },
        { id: "valor-monetario", label: "Valor monetário" },
      ],
      parametro: { id: "qtd-vendas", label: "Qtd. vendas" },
    };
  },

  computed: {
    products() {
      if (Array.isArray(this.dados)) return this.dados;
      if (this.dados && Array.isArray(this.dados.data)) return this.dados.data;
      return [];
    },

    // ✅ ordena desc conforme parâmetro selecionado
    sortedProducts() {
      const arr = [...this.products];
      const key = this.parametro.id === "qtd-vendas" ? "qtd" : "valor";
      return arr.sort((a, b) => {
        const na = Number(a?.[key]) || 0;
        const nb = Number(b?.[key]) || 0;
        if (nb !== na) return nb - na; // desc

        // desempate secundário pela outra métrica
        const other = key === "qtd" ? "valor" : "qtd";
        const oa = Number(a?.[other]) || 0;
        const ob = Number(b?.[other]) || 0;
        if (ob !== oa) return ob - oa;

        // desempate final por nome
        return String(a?.produto || "").localeCompare(String(b?.produto || ""));
      });
    },

    // ✅ usa o maior da lista ordenada para calibrar a barra
    barMax() {
      if (!this.sortedProducts.length) return 1;
      const first = this.sortedProducts[0] || {};
      const raw = this.parametro.id === "qtd-vendas" ? first.qtd : first.valor;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 1;
    },
  },

  methods: {
    updateParameter(param) {
      this.parametro = param;
      // a reatividade dos computed já reordena e recalcula o max
    },
    onApplyPeriodo({ inicio, fim }) {
      this.$emit("change-periodo", { inicio, fim });
    },
    barValue(item) {
      const raw = this.parametro.id === "qtd-vendas" ? item.qtd : item.valor;
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    },
    formatCurrency(v) {
      try {
        return new Intl.NumberFormat("pt-BR", {
          style: "currency",
          currency: "BRL",
        }).format(v ?? 0);
      } catch {
        const num = Number(v ?? 0)
          .toFixed(2)
          .replace(".", ",");
        return `R$ ${num}`;
      }
    },
    medalHexColor(index) {
      switch (index) {
        case 0:
          return "#FFB700";
        case 1:
          return "#D3D3D3";
        case 2:
          return "#CD7F32";
        default:
          return "#F3F6F9";
      }
    },
  },
};
</script>

<style>
.top1-progress-bar .progress-bar {
  background-color: #ffb700;
}
.top2-progress-bar .progress-bar {
  background-color: #d3d3d3;
}
.top3-progress-bar .progress-bar {
  background-color: #cd7f32;
}
.neutral-progress-bar .progress-bar {
  background-color: #f3f6f9;
}

.filtro-dropdown .dropdown-toggle {
  color: var(--primary-color);
}
</style>
